/**
 * Tiny sk/en dictionary for the visitor-facing chrome (wave3 U4.1).
 *
 * Scope: ONLY visible chrome (buttons, labels, hints, placeholders, warn
 * titles/bodies, HUD/legend copy, tool-chip labels). Wire text stays ENGLISH —
 * tap seeds (`Take me to … [#id]`), recovery sends and the accessible-reroute
 * instructions are for the model, which answers in the user's language anyway.
 *
 * Locale selection (resolved ONCE at module load):
 *   1. `?lang=sk|en` URL override → persisted in sessionStorage;
 *   2. a previously persisted override;
 *   3. `navigator.language` starting with "sk" → sk; anything else → en.
 */

export type Locale = "sk" | "en" | "fi" | "ar";

const LANG_KEY = "wf.lang";
const LOCALES: readonly Locale[] = ["sk", "en", "fi", "ar"];

function isLocale(v: string | null): v is Locale {
  return v !== null && (LOCALES as readonly string[]).includes(v);
}

function detect(): Locale {
  try {
    const q = new URLSearchParams(window.location.search).get("lang");
    if (isLocale(q)) {
      sessionStorage.setItem(LANG_KEY, q);
      return q;
    }
    const stored = sessionStorage.getItem(LANG_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* sessionStorage unavailable — fall through to navigator */
  }
  const nav =
    typeof navigator !== "undefined" ? (navigator.language ?? "").toLowerCase() : "";
  for (const l of LOCALES) if (l !== "en" && nav.startsWith(l)) return l;
  return "en";
}

/** The active UI locale, fixed for the app lifetime. */
export const locale: Locale = typeof window === "undefined" ? "en" : detect();

// Reflect the locale on the document for a11y/UA hints. Layout stays LTR (full
// RTL mirroring is a follow-up); Arabic TEXT renders correctly via dir="auto"/
// "rtl" on the chat surfaces themselves.
if (typeof document !== "undefined") {
  document.documentElement.lang = locale;
}

/* ── Dictionary ──────────────────────────────────────────────────────────── */

const en = {
  /* Topbar */
  browsePlaces: "Browse places",
  selectVenue: "Select venue",
  loadingVenues: "Loading venues",
  venueAria: "Venue: {name}",
  searchPlaces: "Search places…",
  searchPlacesLabel: "Search places",
  searchPlacesAria: "Search places (Command-K)",
  accessibleToggle: "Accessible",
  accessibleToggleAria: "Accessible (step-free) routes",

  /* Chat card shell */
  askAssistant: "Ask the assistant",
  assistantTitle: "Wayfinding Assistant",
  assistantPanelAria: "Wayfinding assistant",
  atLabel: "At {label} · Level {n}",
  positionNotSet: "Position not set",
  collapseAssistant: "Collapse assistant",
  nudgeTip: "Ready when you are — ask me to guide you anywhere.",
  jumpLatest: "Jump to latest messages",

  /* Chat input + typeahead */
  inputPlaceholder: "Ask where you want to go…",
  inputAria: "Ask the wayfinding assistant",
  thinkingPlaceholder: "Thinking…",
  thinkingAria: "Thinking",
  send: "Send",
  stop: "Stop",
  stopGenerating: "Stop generating",
  placeSuggestions: "Place suggestions",
  levelN: "Level {n}",

  /* First run */
  firstRunTitle: "Where would you like to go?",
  firstRunSub: "Ask in your own words and I’ll draw the route on the map.",
  startingFrom: "Starting from {label} · Level {n}",
  setStartFirst: "Set your starting point first",
  setStartBody:
    "Tap the map where you are — I’ll ask this again automatically once your position is set.",
  setMyPosition: "Set my position",
  tryAsking: "TRY ASKING",
  prompt1: "Nearest restroom?",
  prompt2: "Where’s the closest café?",
  prompt3: "What clothing stores are here?",
  prompt4: "Where can I eat?",

  /* Options / directory card */
  whichOne: "Which one?",
  chooseDestination: "Choose a destination",
  places: "Places",
  filterPlaceholder: "Filter…",
  filterPlaces: "Filter places",
  clearFilter: "Clear filter",
  noMatchesFor: "No matches for “{q}”",
  matchesCount: "{n} places match",
  noMatches: "No places match",
  tapAPlace: "Tap a place and I’ll take you there",
  renovation: "Renovation",
  underRenovation: "{name}, under renovation",
  levelUpper: "LEVEL {n}",
  otherUpper: "OTHER",

  /* Place card */
  takeMeThere: "Take me there",
  showOnMap: "Show on map",
  mAway: "~{n} m away",

  /* Route card */
  statDistance: "DISTANCE",
  statEta: "EST. TIME",
  statFloors: "FLOORS",
  statRemaining: "REMAINING",
  statTimeLeft: "TIME LEFT",
  walkProgressAria: "Walk progress",
  accessibleStepFree: "Accessible (step-free)",
  accessibleSwitchAria: "Accessible (step-free) route",
  start: "Start",
  badgeStepFree: "STEP-FREE",
  badgeFastest: "FASTEST",
  sendToPhone: "Send this route to your phone",
  sendToPhoneShort: "Send to phone",

  /* Share dialog */
  shareTitle: "Take this route with you",
  shareScan: "Scan to open this route on your phone",
  shareSignIn: "Sign-in required",
  shareAlt: "QR code opening the route to {name} on your phone",

  /* Quick replies + recovery */
  accessibleRoute: "Accessible route",
  findAnother: "Find another",
  startOver: "Start over",
  followUps: "Follow-up suggestions",
  tryAgain: "Try again",
  rephrase: "Rephrase",
  findNearestExit: "Find nearest exit",

  /* Store warnings */
  sessionExpiredTitle: "Session expired",
  sessionExpiredBody:
    "Your session has expired. Please sign in again to keep using the assistant.",
  unavailableTitle: "Assistant unavailable",
  unavailableBody:
    "The assistant can’t be reached right now. Please try again in a moment.",
  wrongTitle: "Something went wrong",
  wrongBody: "I couldn’t reach the wayfinding assistant. Please try again.",
  recomputingStepFree: "recomputing step-free route…",
  recomputingFastest: "recomputing fastest route…",
  computingAccessible: "computing accessible route…",
  computingFastest: "computing fastest route…",

  /* Tool activity */
  toolSearch: "Searching the venue…",
  toolNearest: "Finding the nearest…",
  toolRoute: "Drawing your route…",
  toolOptions: "Gathering options…",
  toolStepFree: "Finding a step-free route…",
  toolFloors: "Checking the floors…",
  toolOrigin: "Setting your starting point…",
  toolReady: "Getting ready…",
  toolWorking: "Working…",
  collapseActivity: "Collapse assistant activity",
  showSteps: "Show the steps the assistant took",

  /* Map HUD */
  pickBanner: "Tap anywhere to place your position",
  positionSetAria: "Position set on Level {n}. Move your position.",
  positionSet: "Position set",
  move: "Move",
  clickMapToSet: "Click the map to set your position",
  pinnedLocation: "Pinned location",
  sharedLocation: "Shared location",
  reCenter: "Re-center",
  getDirections: "Get directions",
  indoorMap: "Indoor map",
  floorSwitcherAria: "Floor switcher, viewing level {n}{routeNote}",
  routeOnLevels: "; route on levels {list}",
  levelOnRoute: "Level {n} (on route)",

  /* Route legend */
  routeLegend: "ROUTE LEGEND",
  endRoute: "End route",
  pathOnFloor: "Path on this floor",
  yourPosition: "Your position",
  destination: "Destination",
  singleFloor: "Single-floor route",
  floorChangeGeneric: "Floor change",
  floorChange: "{mode} (floor change)",
  elevator: "Elevator",
  escalator: "Escalator",
  stairs: "Stairs",

  /* Walk banner */
  dismissWalkBanner: "Dismiss walk banner",

  /* Kiosk attract */
  touchToStart: "Touch to start",
  restartingIn: "Restarting in {n}s",

  /* Place directory (PoiBrowser) */
  directory: "Directory",
  placeDirectory: "Place directory",
  closeDirectory: "Close directory",
  directoryError: "Couldn’t load the directory.",
  noPlacesToBrowse: "No places to browse yet.",
  noPlacesFound: "No places found.",
  searchResults: "Search results",
  floorGround: "Ground",

  /* Voice input (opt-in) */
  voiceInput: "Voice input",
  voiceListening: "Listening… tap to stop",
  voiceConsentBody:
    "Voice input uses your browser’s speech service — audio may be processed by its provider. Continue?",
  voiceAllow: "Allow",
  voiceCancel: "Cancel",

  /* Arrival */
  youHaveArrivedAt: "You’ve arrived — {name} is right here. 🎉",
  youHaveArrived: "You’ve arrived at your destination. 🎉",
} as const;

export type MsgKey = keyof typeof en;

const sk: Record<MsgKey, string> = {
  /* Topbar */
  browsePlaces: "Prehliadať miesta",
  selectVenue: "Vyberte areál",
  loadingVenues: "Načítavam areály",
  venueAria: "Areál: {name}",
  searchPlaces: "Hľadať miesta…",
  searchPlacesLabel: "Hľadať miesta",
  searchPlacesAria: "Hľadať miesta (Command-K)",
  accessibleToggle: "Bezbariérové",
  accessibleToggleAria: "Bezbariérové trasy (bez schodov)",

  /* Chat card shell */
  askAssistant: "Opýtajte sa asistenta",
  assistantTitle: "Navigačný asistent",
  assistantPanelAria: "Navigačný asistent",
  atLabel: "Pri {label} · Podlažie {n}",
  positionNotSet: "Poloha nie je nastavená",
  collapseAssistant: "Zbaliť asistenta",
  nudgeTip: "Som pripravený — opýtajte sa a dovediem vás kamkoľvek.",
  jumpLatest: "Prejsť na najnovšie správy",

  /* Chat input + typeahead */
  inputPlaceholder: "Napíšte, kam chcete ísť…",
  inputAria: "Opýtajte sa navigačného asistenta",
  thinkingPlaceholder: "Premýšľam…",
  thinkingAria: "Premýšľam",
  send: "Odoslať",
  stop: "Zastaviť",
  stopGenerating: "Zastaviť generovanie",
  placeSuggestions: "Návrhy miest",
  levelN: "Podlažie {n}",

  /* First run */
  firstRunTitle: "Kam by ste chceli ísť?",
  firstRunSub: "Opýtajte sa vlastnými slovami a nakreslím trasu na mape.",
  startingFrom: "Štart: {label} · Podlažie {n}",
  setStartFirst: "Najprv nastavte svoj východiskový bod",
  setStartBody:
    "Ťuknite na mapu tam, kde sa nachádzate — otázku položím znova automaticky, len čo bude vaša poloha nastavená.",
  setMyPosition: "Nastaviť moju polohu",
  tryAsking: "SKÚSTE SA OPÝTAŤ",
  prompt1: "Najbližšie toalety?",
  prompt2: "Kde je najbližšia kaviareň?",
  prompt3: "Aké obchody s oblečením tu sú?",
  prompt4: "Kde sa dá najesť?",

  /* Options / directory card */
  whichOne: "Ktoré z nich?",
  chooseDestination: "Vyberte cieľ",
  places: "Miesta",
  filterPlaceholder: "Filtrovať…",
  filterPlaces: "Filtrovať miesta",
  clearFilter: "Zrušiť filter",
  noMatchesFor: "Žiadne výsledky pre „{q}“",
  matchesCount: "{n} miest zodpovedá",
  noMatches: "Žiadne miesta nezodpovedajú",
  tapAPlace: "Ťuknite na miesto a dovediem vás tam",
  renovation: "Rekonštrukcia",
  underRenovation: "{name}, v rekonštrukcii",
  levelUpper: "PODLAŽIE {n}",
  otherUpper: "OSTATNÉ",

  /* Place card */
  takeMeThere: "Naviguj ma tam",
  showOnMap: "Ukázať na mape",
  mAway: "~{n} m odtiaľto",

  /* Route card */
  statDistance: "VZDIALENOSŤ",
  statEta: "ODHAD. ČAS",
  statFloors: "PODLAŽIA",
  statRemaining: "ZOSTÁVA",
  statTimeLeft: "ZOSTÁVAJÚCI ČAS",
  walkProgressAria: "Priebeh trasy",
  accessibleStepFree: "Bezbariérová (bez schodov)",
  accessibleSwitchAria: "Bezbariérová trasa (bez schodov)",
  start: "Štart",
  badgeStepFree: "BEZBARIÉROVÁ",
  badgeFastest: "NAJRÝCHLEJŠIA",
  sendToPhone: "Poslať túto trasu do telefónu",
  sendToPhoneShort: "Poslať do telefónu",

  /* Share dialog */
  shareTitle: "Vezmite si trasu so sebou",
  shareScan: "Naskenujte a otvorte túto trasu v telefóne",
  shareSignIn: "Vyžaduje sa prihlásenie",
  shareAlt: "QR kód otvárajúci trasu do {name} v telefóne",

  /* Quick replies + recovery */
  accessibleRoute: "Bezbariérová trasa",
  findAnother: "Nájsť inú",
  startOver: "Začať odznova",
  followUps: "Ďalšie návrhy",
  tryAgain: "Skúsiť znova",
  rephrase: "Preformulovať",
  findNearestExit: "Nájsť najbližší východ",

  /* Store warnings */
  sessionExpiredTitle: "Relácia vypršala",
  sessionExpiredBody:
    "Vaša relácia vypršala. Prihláste sa znova, aby ste mohli asistenta ďalej používať.",
  unavailableTitle: "Asistent je nedostupný",
  unavailableBody: "Asistent je momentálne nedostupný. Skúste to o chvíľu znova.",
  wrongTitle: "Niečo sa pokazilo",
  wrongBody: "Nepodarilo sa mi spojiť s navigačným asistentom. Skúste to znova.",
  recomputingStepFree: "prepočítavam bezbariérovú trasu…",
  recomputingFastest: "prepočítavam najrýchlejšiu trasu…",
  computingAccessible: "počítam bezbariérovú trasu…",
  computingFastest: "počítam najrýchlejšiu trasu…",

  /* Tool activity */
  toolSearch: "Prehľadávam areál…",
  toolNearest: "Hľadám najbližšie…",
  toolRoute: "Kreslím vašu trasu…",
  toolOptions: "Zbieram možnosti…",
  toolStepFree: "Hľadám bezbariérovú trasu…",
  toolFloors: "Kontrolujem podlažia…",
  toolOrigin: "Nastavujem váš východiskový bod…",
  toolReady: "Pripravujem sa…",
  toolWorking: "Pracujem…",
  collapseActivity: "Zbaliť aktivitu asistenta",
  showSteps: "Zobraziť kroky asistenta",

  /* Map HUD */
  pickBanner: "Ťuknite kamkoľvek na mapu a umiestnite svoju polohu",
  positionSetAria: "Poloha nastavená na podlaží {n}. Presunúť polohu.",
  positionSet: "Poloha nastavená",
  move: "Presunúť",
  clickMapToSet: "Kliknite na mapu a nastavte svoju polohu",
  pinnedLocation: "Pripnutá poloha",
  sharedLocation: "Zdieľaná poloha",
  reCenter: "Vycentrovať",
  getDirections: "Navigovať",
  indoorMap: "Vnútorná mapa",
  floorSwitcherAria: "Prepínač podlaží, zobrazené podlažie {n}{routeNote}",
  routeOnLevels: "; trasa na podlažiach {list}",
  levelOnRoute: "Podlažie {n} (na trase)",

  /* Route legend */
  routeLegend: "LEGENDA TRASY",
  endRoute: "Ukončiť trasu",
  pathOnFloor: "Trasa na tomto podlaží",
  yourPosition: "Vaša poloha",
  destination: "Cieľ",
  singleFloor: "Trasa po jednom podlaží",
  floorChangeGeneric: "Zmena podlažia",
  floorChange: "{mode} (zmena podlažia)",
  elevator: "Výťah",
  escalator: "Eskalátor",
  stairs: "Schody",

  /* Walk banner */
  dismissWalkBanner: "Skryť navigačný panel",

  /* Kiosk attract */
  touchToStart: "Dotknite sa a začnite",
  restartingIn: "Reštart o {n} s",

  /* Place directory (PoiBrowser) */
  directory: "Adresár",
  placeDirectory: "Adresár miest",
  closeDirectory: "Zavrieť adresár",
  directoryError: "Adresár sa nepodarilo načítať.",
  noPlacesToBrowse: "Zatiaľ tu nie sú žiadne miesta.",
  noPlacesFound: "Nenašli sa žiadne miesta.",
  searchResults: "Výsledky vyhľadávania",
  floorGround: "Prízemie",

  /* Voice input (opt-in) */
  voiceInput: "Hlasový vstup",
  voiceListening: "Počúvam… ťuknutím zastavíte",
  voiceConsentBody:
    "Hlasový vstup používa rečovú službu prehliadača — zvuk môže spracovať jej poskytovateľ. Pokračovať?",
  voiceAllow: "Povoliť",
  voiceCancel: "Zrušiť",

  /* Arrival */
  youHaveArrivedAt: "Ste na mieste — {name} je priamo tu. 🎉",
  youHaveArrived: "Dorazili ste do cieľa. 🎉",
};

const fi: Record<MsgKey, string> = {
  browsePlaces: "Selaa paikkoja",
  selectVenue: "Valitse kohde",
  loadingVenues: "Ladataan kohteita",
  venueAria: "Kohde: {name}",
  searchPlaces: "Hae paikkoja…",
  searchPlacesLabel: "Hae paikkoja",
  searchPlacesAria: "Hae paikkoja (Command-K)",
  accessibleToggle: "Esteetön",
  accessibleToggleAria: "Esteettömät (portaattomat) reitit",

  askAssistant: "Kysy avustajalta",
  assistantTitle: "Opastusavustaja",
  assistantPanelAria: "Opastusavustaja",
  atLabel: "Sijainti {label} · Kerros {n}",
  positionNotSet: "Sijaintia ei asetettu",
  collapseAssistant: "Pienennä avustaja",
  nudgeTip: "Valmiina kun sinä olet — kysy, niin opastan sinut perille.",
  jumpLatest: "Siirry uusimpiin viesteihin",

  inputPlaceholder: "Kysy, minne haluat mennä…",
  inputAria: "Kysy opastusavustajalta",
  thinkingPlaceholder: "Mietin…",
  thinkingAria: "Mietin",
  send: "Lähetä",
  stop: "Pysäytä",
  stopGenerating: "Keskeytä vastaus",
  placeSuggestions: "Paikkaehdotukset",
  levelN: "Kerros {n}",

  firstRunTitle: "Minne haluaisit mennä?",
  firstRunSub: "Kysy omin sanoin, niin piirrän reitin kartalle.",
  startingFrom: "Lähtöpiste {label} · Kerros {n}",
  setStartFirst: "Aseta ensin lähtöpisteesi",
  setStartBody:
    "Napauta karttaa kohdassa, jossa olet — kysyn tämän automaattisesti uudelleen, kun sijaintisi on asetettu.",
  setMyPosition: "Aseta sijaintini",
  tryAsking: "KOKEILE KYSYÄ",
  prompt1: "Lähin WC?",
  prompt2: "Missä on lähin kahvila?",
  prompt3: "Mitä vaateliikkeitä täällä on?",
  prompt4: "Missä voin syödä?",

  whichOne: "Mikä näistä?",
  chooseDestination: "Valitse kohde",
  places: "Paikat",
  filterPlaceholder: "Suodata…",
  filterPlaces: "Suodata paikkoja",
  clearFilter: "Tyhjennä suodatin",
  noMatchesFor: "Ei osumia haulle ”{q}”",
  matchesCount: "{n} paikkaa löytyi",
  noMatches: "Ei osumia",
  tapAPlace: "Napauta paikkaa, niin vien sinut sinne",
  renovation: "Remontissa",
  underRenovation: "{name}, remontissa",
  levelUpper: "KERROS {n}",
  otherUpper: "MUUT",

  takeMeThere: "Vie minut sinne",
  showOnMap: "Näytä kartalla",
  mAway: "~{n} m päässä",

  statDistance: "MATKA",
  statEta: "ARVIOITU AIKA",
  statFloors: "KERROKSET",
  statRemaining: "JÄLJELLÄ",
  statTimeLeft: "AIKAA JÄLJELLÄ",
  walkProgressAria: "Kävelyn eteneminen",
  accessibleStepFree: "Esteetön (portaaton)",
  accessibleSwitchAria: "Esteetön (portaaton) reitti",
  start: "Aloita",
  badgeStepFree: "PORTAATON",
  badgeFastest: "NOPEIN",
  sendToPhone: "Lähetä reitti puhelimeesi",
  sendToPhoneShort: "Lähetä puhelimeen",

  shareTitle: "Ota reitti mukaasi",
  shareScan: "Skannaa avataksesi reitin puhelimessasi",
  shareSignIn: "Vaatii kirjautumisen",
  shareAlt: "QR-koodi, joka avaa reitin kohteeseen {name} puhelimessasi",

  accessibleRoute: "Esteetön reitti",
  findAnother: "Etsi toinen",
  startOver: "Aloita alusta",
  followUps: "Jatkoehdotukset",
  tryAgain: "Yritä uudelleen",
  rephrase: "Muotoile uudelleen",
  findNearestExit: "Etsi lähin uloskäynti",

  sessionExpiredTitle: "Istunto vanhentui",
  sessionExpiredBody:
    "Istuntosi on vanhentunut. Kirjaudu sisään uudelleen jatkaaksesi avustajan käyttöä.",
  unavailableTitle: "Avustaja ei ole käytettävissä",
  unavailableBody: "Avustajaan ei juuri nyt saada yhteyttä. Yritä hetken kuluttua uudelleen.",
  wrongTitle: "Jokin meni pieleen",
  wrongBody: "En saanut yhteyttä opastusavustajaan. Yritä uudelleen.",
  recomputingStepFree: "lasketaan portaatonta reittiä…",
  recomputingFastest: "lasketaan nopeinta reittiä…",
  computingAccessible: "lasketaan esteetöntä reittiä…",
  computingFastest: "lasketaan nopeinta reittiä…",

  toolSearch: "Haetaan kohteesta…",
  toolNearest: "Etsitään lähintä…",
  toolRoute: "Piirretään reittiäsi…",
  toolOptions: "Kootaan vaihtoehtoja…",
  toolStepFree: "Etsitään portaatonta reittiä…",
  toolFloors: "Tarkistetaan kerroksia…",
  toolOrigin: "Asetetaan lähtöpistettäsi…",
  toolReady: "Valmistellaan…",
  toolWorking: "Työskentelen…",
  collapseActivity: "Pienennä avustajan toiminta",
  showSteps: "Näytä avustajan tekemät vaiheet",

  pickBanner: "Napauta mihin tahansa asettaaksesi sijaintisi",
  positionSetAria: "Sijainti asetettu kerrokseen {n}. Siirrä sijaintiasi.",
  positionSet: "Sijainti asetettu",
  move: "Siirrä",
  clickMapToSet: "Napsauta karttaa asettaaksesi sijaintisi",
  pinnedLocation: "Merkitty sijainti",
  sharedLocation: "Jaettu sijainti",
  reCenter: "Keskitä",
  getDirections: "Hae reittiohjeet",
  indoorMap: "Sisätilakartta",
  floorSwitcherAria: "Kerrosvalitsin, näkyvissä kerros {n}{routeNote}",
  routeOnLevels: "; reitti kerroksissa {list}",
  levelOnRoute: "Kerros {n} (reitillä)",

  routeLegend: "REITIN SELITE",
  endRoute: "Lopeta reitti",
  pathOnFloor: "Reitti tässä kerroksessa",
  yourPosition: "Sijaintisi",
  destination: "Määränpää",
  singleFloor: "Yhden kerroksen reitti",
  floorChangeGeneric: "Kerrosvaihto",
  floorChange: "{mode} (kerrosvaihto)",
  elevator: "Hissi",
  escalator: "Rullaportaat",
  stairs: "Portaat",

  dismissWalkBanner: "Sulje kävelypalkki",

  touchToStart: "Kosketa aloittaaksesi",
  restartingIn: "Käynnistyy uudelleen {n} s",

  directory: "Hakemisto",
  placeDirectory: "Paikkahakemisto",
  closeDirectory: "Sulje hakemisto",
  directoryError: "Hakemistoa ei voitu ladata.",
  noPlacesToBrowse: "Täällä ei ole vielä paikkoja selattavaksi.",
  noPlacesFound: "Paikkoja ei löytynyt.",
  searchResults: "Hakutulokset",
  floorGround: "Katutaso",

  voiceInput: "Puhesyöttö",
  voiceListening: "Kuuntelen… napauta lopettaaksesi",
  voiceConsentBody:
    "Puhesyöttö käyttää selaimesi puhepalvelua — sen tarjoaja voi käsitellä ääntä. Jatketaanko?",
  voiceAllow: "Salli",
  voiceCancel: "Peruuta",

  /* Arrival */
  youHaveArrivedAt: "Olet perillä — {name} on tässä. 🎉",
  youHaveArrived: "Olet saapunut määränpäähäsi. 🎉",
};

const ar: Record<MsgKey, string> = {
  browsePlaces: "تصفح الأماكن",
  selectVenue: "اختر الموقع",
  loadingVenues: "جارٍ تحميل المواقع",
  venueAria: "الموقع: {name}",
  searchPlaces: "ابحث عن الأماكن…",
  searchPlacesLabel: "ابحث عن الأماكن",
  searchPlacesAria: "ابحث عن الأماكن (Command-K)",
  accessibleToggle: "ميسّر",
  accessibleToggleAria: "مسارات ميسّرة (بدون درج)",

  askAssistant: "اسأل المساعد",
  assistantTitle: "مساعد الإرشاد",
  assistantPanelAria: "مساعد الإرشاد",
  atLabel: "عند {label} · الطابق {n}",
  positionNotSet: "لم يتم تحديد الموقع",
  collapseAssistant: "طيّ المساعد",
  nudgeTip: "جاهز متى كنت جاهزًا — اسألني وسأرشدك إلى أي مكان.",
  jumpLatest: "الانتقال إلى أحدث الرسائل",

  inputPlaceholder: "اسأل إلى أين تريد الذهاب…",
  inputAria: "اسأل مساعد الإرشاد",
  thinkingPlaceholder: "أفكر…",
  thinkingAria: "أفكر",
  send: "إرسال",
  stop: "إيقاف",
  stopGenerating: "إيقاف الإجابة",
  placeSuggestions: "اقتراحات الأماكن",
  levelN: "الطابق {n}",

  firstRunTitle: "إلى أين تودّ الذهاب؟",
  firstRunSub: "اسأل بكلماتك وسأرسم لك المسار على الخريطة.",
  startingFrom: "الانطلاق من {label} · الطابق {n}",
  setStartFirst: "حدّد نقطة انطلاقك أولاً",
  setStartBody: "انقر على الخريطة حيث تقف — سأطرح سؤالك تلقائيًا بعد تحديد موقعك.",
  setMyPosition: "حدّد موقعي",
  tryAsking: "جرّب أن تسأل",
  prompt1: "أين أقرب دورة مياه؟",
  prompt2: "أين أقرب مقهى؟",
  prompt3: "ما متاجر الملابس الموجودة هنا؟",
  prompt4: "أين يمكنني أن آكل؟",

  whichOne: "أيّها تقصد؟",
  chooseDestination: "اختر وجهة",
  places: "الأماكن",
  filterPlaceholder: "تصفية…",
  filterPlaces: "تصفية الأماكن",
  clearFilter: "مسح التصفية",
  noMatchesFor: "لا نتائج لـ «{q}»",
  matchesCount: "{n} أماكن مطابقة",
  noMatches: "لا توجد أماكن مطابقة",
  tapAPlace: "انقر على مكان وسأصحبك إليه",
  renovation: "قيد التجديد",
  underRenovation: "{name}، قيد التجديد",
  levelUpper: "الطابق {n}",
  otherUpper: "أخرى",

  takeMeThere: "خذني إلى هناك",
  showOnMap: "أظهر على الخريطة",
  mAway: "على بعد ~{n} م",

  statDistance: "المسافة",
  statEta: "الوقت المقدّر",
  statFloors: "الطوابق",
  statRemaining: "المتبقي",
  statTimeLeft: "الوقت المتبقي",
  walkProgressAria: "تقدّم المسير",
  accessibleStepFree: "ميسّر (بدون درج)",
  accessibleSwitchAria: "مسار ميسّر (بدون درج)",
  start: "ابدأ",
  badgeStepFree: "بدون درج",
  badgeFastest: "الأسرع",
  sendToPhone: "أرسل هذا المسار إلى هاتفك",
  sendToPhoneShort: "أرسل إلى الهاتف",

  shareTitle: "خذ المسار معك",
  shareScan: "امسح الرمز لفتح المسار على هاتفك",
  shareSignIn: "يتطلب تسجيل الدخول",
  shareAlt: "رمز QR يفتح المسار إلى {name} على هاتفك",

  accessibleRoute: "مسار ميسّر",
  findAnother: "ابحث عن آخر",
  startOver: "ابدأ من جديد",
  followUps: "اقتراحات المتابعة",
  tryAgain: "حاول مرة أخرى",
  rephrase: "أعد الصياغة",
  findNearestExit: "ابحث عن أقرب مخرج",

  sessionExpiredTitle: "انتهت الجلسة",
  sessionExpiredBody: "انتهت صلاحية جلستك. يرجى تسجيل الدخول مجددًا لمتابعة استخدام المساعد.",
  unavailableTitle: "المساعد غير متاح",
  unavailableBody: "يتعذّر الوصول إلى المساعد حاليًا. يرجى المحاولة بعد قليل.",
  wrongTitle: "حدث خطأ ما",
  wrongBody: "تعذّر الوصول إلى مساعد الإرشاد. يرجى المحاولة مرة أخرى.",
  recomputingStepFree: "إعادة حساب المسار الميسّر…",
  recomputingFastest: "إعادة حساب أسرع مسار…",
  computingAccessible: "جارٍ حساب المسار الميسّر…",
  computingFastest: "جارٍ حساب أسرع مسار…",

  toolSearch: "جارٍ البحث في الموقع…",
  toolNearest: "جارٍ إيجاد الأقرب…",
  toolRoute: "جارٍ رسم مسارك…",
  toolOptions: "جارٍ جمع الخيارات…",
  toolStepFree: "جارٍ إيجاد مسار بدون درج…",
  toolFloors: "جارٍ فحص الطوابق…",
  toolOrigin: "جارٍ تحديد نقطة انطلاقك…",
  toolReady: "جارٍ التحضير…",
  toolWorking: "أعمل على ذلك…",
  collapseActivity: "طيّ نشاط المساعد",
  showSteps: "أظهر خطوات المساعد",

  pickBanner: "انقر في أي مكان لتحديد موقعك",
  positionSetAria: "تم تحديد الموقع في الطابق {n}. انقل موقعك.",
  positionSet: "تم تحديد الموقع",
  move: "نقل",
  clickMapToSet: "انقر على الخريطة لتحديد موقعك",
  pinnedLocation: "موقع مثبّت",
  sharedLocation: "موقع مُشارَك",
  reCenter: "إعادة التمركز",
  getDirections: "احصل على الاتجاهات",
  indoorMap: "خريطة داخلية",
  floorSwitcherAria: "مبدّل الطوابق، عرض الطابق {n}{routeNote}",
  routeOnLevels: "؛ المسار في الطوابق {list}",
  levelOnRoute: "الطابق {n} (على المسار)",

  routeLegend: "مفتاح المسار",
  endRoute: "إنهاء المسار",
  pathOnFloor: "المسار في هذا الطابق",
  yourPosition: "موقعك",
  destination: "الوجهة",
  singleFloor: "مسار في طابق واحد",
  floorChangeGeneric: "تغيير الطابق",
  floorChange: "{mode} (تغيير الطابق)",
  elevator: "مصعد",
  escalator: "سلّم متحرك",
  stairs: "درج",

  dismissWalkBanner: "إغلاق شريط المسير",

  touchToStart: "المس للبدء",
  restartingIn: "إعادة التشغيل خلال {n} ث",

  directory: "الدليل",
  placeDirectory: "دليل الأماكن",
  closeDirectory: "إغلاق الدليل",
  directoryError: "تعذّر تحميل الدليل.",
  noPlacesToBrowse: "لا توجد أماكن للتصفح بعد.",
  noPlacesFound: "لم يُعثر على أماكن.",
  searchResults: "نتائج البحث",
  floorGround: "الطابق الأرضي",

  voiceInput: "الإدخال الصوتي",
  voiceListening: "أستمع… انقر للإيقاف",
  voiceConsentBody:
    "يستخدم الإدخال الصوتي خدمة الكلام في متصفحك — وقد يعالج مزوّدها الصوت. هل تريد المتابعة؟",
  voiceAllow: "سماح",
  voiceCancel: "إلغاء",

  /* Arrival */
  youHaveArrivedAt: "لقد وصلت — {name} هنا تمامًا. 🎉",
  youHaveArrived: "لقد وصلت إلى وجهتك. 🎉",
};

const dicts: Record<Locale, Record<MsgKey, string>> = { en, sk, fi, ar };

/* ── API ─────────────────────────────────────────────────────────────────── */

/**
 * Look up a chrome string in the active locale; `{name}`-style placeholders
 * are substituted from `vars`.
 */
export function t(key: MsgKey, vars?: Record<string, string | number>): string {
  const msg = dicts[locale][key];
  if (!vars) return msg;
  return msg.replace(/\{(\w+)\}/g, (m, k: string) =>
    k in vars ? String(vars[k]) : m,
  );
}

/** First-run suggested prompts (chrome — the tapped text IS what gets sent). */
export function suggestedPrompts(): string[] {
  return [t("prompt1"), t("prompt2"), t("prompt3"), t("prompt4")];
}

/** "N steps" with per-locale plural forms. */
export function stepsLabel(n: number): string {
  switch (locale) {
    case "sk":
      if (n === 1) return "1 krok";
      if (n >= 2 && n <= 4) return `${n} kroky`;
      return `${n} krokov`;
    case "fi":
      return n === 1 ? "1 vaihe" : `${n} vaihetta`;
    case "ar":
      if (n === 1) return "خطوة واحدة";
      if (n === 2) return "خطوتان";
      return `${n} خطوات`;
    default:
      return `${n} ${n === 1 ? "step" : "steps"}`;
  }
}
